const DIFFICULTY_SCALE = {
    easy: 0,
    medium: 1,
    hard: 2,
};

const DIFFICULTY_KEYWORDS = Object.keys(DIFFICULTY_SCALE);

export const normalizeDifficulty = (exercise) => {
    const rawDifficulty = (exercise?.difficulty || '').toString().toLowerCase().trim();
    if (DIFFICULTY_SCALE[rawDifficulty] !== undefined) {
        return rawDifficulty;
    }

    if (Array.isArray(exercise?.tags)) {
        const fromTag = exercise.tags
            .map((tag) => tag?.toString().toLowerCase().trim())
            .find((tag) => DIFFICULTY_KEYWORDS.includes(tag));
        if (fromTag) return fromTag;
    }

    return 'medium';
};

const matchesLanguage = (exercise, userLanguage) => {
    if (!userLanguage) return true;
    if (!exercise?.language) return true;
    return exercise.language === userLanguage;
};

const matchesSchoolLevel = (exercise, userSchoolLevel) => {
    if (!userSchoolLevel) return true;
    const exerciseLevel = exercise?.school_level || exercise?.education_level || exercise?.grade_level;
    if (!exerciseLevel) return true;
    return exerciseLevel === userSchoolLevel;
};

export const inferRecommendationMode = (submissions = [], exercises = []) => {
    if (!Array.isArray(submissions) || submissions.length < 3 || !Array.isArray(exercises) || exercises.length === 0) {
        return 'balanced';
    }

    const exerciseIds = new Set(exercises.map((exercise) => exercise.id));
    const recentUnitSubmissions = submissions
        .filter((submission) => exerciseIds.has(submission.exercise_id))
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .slice(0, 8);

    if (recentUnitSubmissions.length < 3) {
        return 'balanced';
    }

    const correctCount = recentUnitSubmissions.filter((submission) => submission.is_correct).length;
    const successRate = correctCount / recentUnitSubmissions.length;

    if (successRate <= 0.4) return 'easier';
    if (successRate >= 0.8) return 'harder';
    return 'balanced';
};

export const rankExercisesByRecommendation = (exercises = [], recommendationMode = 'balanced') => {
    const targetDifficulty = recommendationMode === 'easier'
        ? 'easy'
        : recommendationMode === 'harder'
            ? 'hard'
            : 'medium';

    return exercises
        .map((exercise, index) => ({ exercise, index }))
        .sort((left, right) => {
            const leftDistance = Math.abs(DIFFICULTY_SCALE[normalizeDifficulty(left.exercise)] - DIFFICULTY_SCALE[targetDifficulty]);
            const rightDistance = Math.abs(DIFFICULTY_SCALE[normalizeDifficulty(right.exercise)] - DIFFICULTY_SCALE[targetDifficulty]);
            if (leftDistance !== rightDistance) return leftDistance - rightDistance;
            return left.index - right.index;
        })
        .map((item) => item.exercise);
};

export const selectRecommendedExercises = ({
    exercises = [],
    userLanguage,
    userSchoolLevel,
    submissions = [],
}) => {
    const strictMatches = exercises.filter((exercise) =>
        matchesLanguage(exercise, userLanguage) && matchesSchoolLevel(exercise, userSchoolLevel));
    const candidatePool = strictMatches.length > 0 ? strictMatches : exercises;
    const recommendationMode = inferRecommendationMode(submissions, candidatePool);

    return {
        recommendationMode,
        exercises: rankExercisesByRecommendation(candidatePool, recommendationMode),
    };
};
